import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import IntervalCell from '../components/IntervalCell';
import CandlestickCell from '../components/CandlestickCell';
import DateCell from '../components/DateCell';
import DirectionCell from '../components/DirectionCell';
import TickerCell from '../components/TickerCell';
import { Trend } from '../backend/interfaces';
import { fetchTrendsData } from '../backend/fetchs';

const TrendsPage: React.FC = () => {
  const [data, setData] = useState<Trend[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const observer = useRef<IntersectionObserver | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await fetchTrendsData();
      
      if (page === 1) {
        setData(result.data);
      } else {
        setData(prev => [...prev, ...result.data]);
      }

      setHasMore(result.data.length > 0);
      setError(null);
    } catch (err) {
      setError('Error fetching data');
      console.error('Error fetching trends data:', err);
    } finally {
      setLoading(false);
    }
  }, [page]);

  const lastElementRef = useCallback((node: HTMLTableRowElement) => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prevPage => prevPage + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (error && data.length === 0) {
    return (
      <div className="text-center">
        <div className="text-red-500 text-xl mb-4">{error}</div>
        <button
          onClick={() => {
            setPage(1);
            setData([]);
            fetchData();
          }}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-2xl font-bold text-gray-800">Trends Data</h2>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ticker
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Confirmed
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Candlestick
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Interval
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((item, index) => (
              <tr 
                key={item.id} 
                ref={data.length === index + 1 ? lastElementRef : null}
                className="hover:bg-gray-50"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  <Link
                    to={`/trends/${item.id}`}
                    className="text-blue-600 hover:text-blue-900 font-medium hover:underline"
                  >
                    {item.id}
                  </Link>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <TickerCell ticker={item.ticker.description} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <DirectionCell direction={item.side.description} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <DateCell timestamp={item.created_at} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <CandlestickCell open_time={item.candlestick.open_time} close_time={item.candlestick.close_time} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <IntervalCell interval={item.candlestick.interval.description} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      )}

      {error && data.length > 0 && (
        <div className="text-center py-4">
          <div className="text-red-500 text-sm mb-2">{error}</div>
          <button
            onClick={() => {
              setPage(1);
              setData([]);
              fetchData();
            }}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-sm"
          >
            Retry
          </button>
        </div>
      )}
    </div>
  );
};

export default TrendsPage; 